<template>
  <section>
    <LoadingIndicator v-if="submitting" />
    <ValidationObserver slim v-slot="{ invalid }">
      <b-form @submit.prevent="submit" novalidate>
        <EwnFormControl vid="username" label-for="username" :label="$t('login.username-label')"
                        v-slot="{ state }" :rules="{ required: true }">
          <b-form-input id="username" name="username" :state="state"
            v-model="credential.username" autofocus autocapitalize="off"
            autocomplete="username" autocorrect="off" spellcheck="false" />
        </EwnFormControl>

        <EwnFormControl vid="password" label-for="password" :label="$t('login.password-label')"
                        v-slot="{ state }" class="password-control" :rules="{ required: true }">
          <b-form-input id="password" type="password" name="password" :state="state"
            v-model="credential.password" autocomplete="current-password" />
          <router-link :to="{ path: '/forgot-password', query: { 'brand_template': brandTemplate }}"
                       class="button-link forgot-password-button">
            {{ $t('login.forgot-password-button') }}
          </router-link>
        </EwnFormControl>

        <EwnFormControl v-if="useTwoFactorAuth" vid="otp" label-for="otp"
                        :label="$t('login.2fa-auth-label')" v-slot="{ state }"
                        :rules="{ required: true, min: 6, max: 8 }">
          <b-form-input ref="authCodeInput" id="otp" name="otp" :state="state"
                        v-model="credential.otp" :placeholder="$t('login.2fa-auth-code-placeholder')"
                        autofocus autocorrect="off" spellcheck="false"></b-form-input>
        </EwnFormControl>

        <div class="invalid-feedback" v-if="errorMessage">{{ errorMessage }}</div>

        <div class="main-button-container">
          <button class="primary" :disabled="invalid || submitting">
            {{ $t('login.login-button') }}
          </button>
          <div class="link-container">
            <a class="button-link" v-if="providerUri" :href="providerUri">
              {{ $t('login.single-sign-on-button') }}
            </a>
            <router-link :to="{ path: '/contact-us', query: { 'brand_template': brandTemplate }}"
                         class="button-link">
              {{ $t('login.contact-button') }}
            </router-link>
          </div>
        </div>

        <footer>
          <p>{{ $t('ewn.copyright-notice', { currentYear: getCurrentYear() }) }}</p>
          <p>{{ $t('ewn.access-disclaimer') }}</p>
        </footer>
      </b-form>
    </ValidationObserver>
  </section>
</template>

<script>
  import { now } from '@ewn/time';
  import { defineComponent, reactive, ref } from 'vue';
  import { BForm, BFormInput } from 'bootstrap-vue';
  import { ValidationObserver } from 'vee-validate';
  import { useLogin } from './useLogin';
  import EwnFormControl from '@/modules/forms/components/EwnFormControl';
  import LoadingIndicator from '@/modules/loading/components/LoadingIndicator';

  export default defineComponent({
    name: 'Login',
    components: {
      'b-form': BForm,
      'b-form-input': BFormInput,
      EwnFormControl,
      LoadingIndicator,
      ValidationObserver
    },
    props: {
      redirectUri: {
        type: String
      },
      providerUri: {
        type: String
      },
      brandTemplate: {
        type: String
      }
    },
    setup (props) {
      const errorMessage = ref(null);
      const submitting = ref(false);

      const credential = reactive({
        username: null,
        password: null,
        otp: null
      });

      const { login } = useLogin({
        redirectUri: props.redirectUri,
        brandTemplate: props.brandTemplate
      });

      const useTwoFactorAuth = ref(false);

      return {
        credential,
        errorMessage,
        getCurrentYear () {
          return now().getFullYear();
        },
        async submit () {
          errorMessage.value = null;
          submitting.value = true;

          const result = await login(credential);
          if (!result) return;

          errorMessage.value = result.message;
          useTwoFactorAuth.value = !!result.showTwoFactorAuth || !!credential.otp;
          submitting.value = false;
        },
        submitting,
        useTwoFactorAuth
      };
    }
  });
</script>

<style lang="scss" scoped>
  section {
    max-width: 400px;
  }

  form {
    width: 100%;
  }

  .password-control::v-deep .form-group {
    position: relative;
  }

  .forgot-password-button {
    position: absolute;
    top: 2px;
    right: 0;
  }

  footer {
    font-size: 10px;
    color: gray;
  }

  footer > :first-child {
    margin: 16px 0;
  }
</style>
